import type { Icon as PhosphorIcon } from '@phosphor-icons/react';
import { SquaresFour, X } from '@phosphor-icons/react';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Button as AriaButton, Link } from 'react-aria-components';
import { IconButton } from '../atoms/Button';
import { Icon } from '../atoms/Icon';
import { snapOpen, useVerticalDrag } from '../lib/drag';
import { cn } from '../lib/cn';
import { uiText } from '../i18n';

export interface TabBarItem {
  id: string;
  label: string;
  icon: PhosphorIcon;
  href: string;
  /** Number of things waiting (e.g. approvals). Announced with the label. */
  badge?: number;
}

export interface TabBarMore {
  /** Every destination, shown as big tiles when the bar is pulled up. */
  items: TabBarItem[];
  /** Smaller links under the tiles (settings, feedback …). */
  links?: TabBarItem[];
}

export interface TabBarProps {
  items: TabBarItem[];
  activeId: string;
  label?: string;
  className?: string;
  /**
   * Room for more (Mike, 14.10.: "lässt sich weiter skalieren"): the bar keeps a few fixed
   * destinations plus "Mehr"; pulling the bar up – or tapping "Mehr" – shows all of them.
   */
  more?: TabBarMore;
}

function Badge({ n }: { n?: number }) {
  return n ? (
    <span
      aria-hidden
      className="absolute -top-2 -right-3 min-w-5 rounded-chip bg-accent-primary px-1 text-center text-xs font-bold text-accent-on-accent"
    >
      {n}
    </span>
  ) : null;
}

const tabClass = (active: boolean) =>
  cn(
    'relative flex min-h-touch-target cursor-pointer flex-col items-center justify-center gap-gap-xs pt-inset-xs text-xs',
    'outline-none data-focus-visible:outline-3 data-focus-visible:-outline-offset-4 data-focus-visible:outline-border-focus',
    active ? 'font-bold text-text-primary' : 'font-medium text-text-muted',
  );

/**
 * Links must not be dragged away (desktop: native drag and drop; phones: link preview on a long
 * press) – that swallowed the swipe on the bar.
 */
const noLinkDrag = '[&_a]:[-webkit-user-drag:none] [&_a]:[-webkit-touch-callout:none]';

function ActiveBar() {
  return <span aria-hidden className="absolute top-0 h-1 w-10 rounded-chip bg-accent-primary" />;
}

function Tabs({
  items,
  activeId,
  disabled,
}: {
  items: TabBarItem[];
  activeId: string;
  disabled?: boolean;
}) {
  return items.map((item) => {
    const active = item.id === activeId;
    return (
      <Link
        key={item.id}
        href={item.href}
        isDisabled={disabled}
        aria-current={active ? 'page' : undefined}
        className={tabClass(active)}
      >
        {active && <ActiveBar />}
        <span className="relative">
          <Icon icon={item.icon} size="md" weight={active ? 'fill' : 'regular'} />
          <Badge n={item.badge} />
        </span>
        {item.label}
        {item.badge ? <span className="sr-only">{uiText().open(String(item.badge))}</span> : null}
      </Link>
    );
  });
}

/**
 * Bottom navigation for phones. Labels are always visible (no icon-only tabs), the active tab
 * carries an accent bar – colour plus weight, never colour alone.
 */
export function TabBar({ items, activeId, label, className, more }: TabBarProps) {
  if (more) return <PullUpBar {...{ items, activeId, label, className, more }} />;
  return (
    <nav
      aria-label={label ?? uiText().mainNav}
      className={cn(
        'grid auto-cols-fr grid-flow-col border-t border-border-subtle bg-surface-raised px-inset-xs pb-safe',
        className,
      )}
    >
      <Tabs items={items} activeId={activeId} />
    </nav>
  );
}

/**
 * The bar itself is pulled up and turns into "Alle Bereiche" (Mike, 15.10.: "dass ich das
 * eigentliche Bottom-Nav hochziehe und es sich dabei transformiert"): one surface that grows with
 * the finger – the tab row fades into the title, the tiles come up underneath – stays where it is
 * held and snaps open or closed on release (a flick counts). Swipe anywhere on it. "Mehr" is a
 * real button, so keyboard and screen reader users open the same thing without a gesture.
 */
function PullUpBar({
  items,
  activeId,
  label,
  className,
  more,
}: TabBarProps & { more: TabBarMore }) {
  const [open, setOpenState] = useState(false);
  // a finger drags right now – the links under it must not count it as a tap
  const [moving, setMoving] = useState(false);
  const [barHeight, setBarHeight] = useState(0);
  // the tiles exist on screen only while open, opening, closing or held
  const [shown, setShown] = useState(false);
  const hideTimer = useRef(0);
  const dock = useRef<HTMLDivElement>(null);
  const bar = useRef<HTMLElement>(null);
  const title = useRef<HTMLDivElement>(null);
  const scrim = useRef<HTMLDivElement>(null);
  const moreButton = useRef<HTMLButtonElement>(null);
  const offset = useRef(0);
  const headingId = useId();

  /** How far the surface grows above the bar. */
  const room = () => {
    const d = dock.current;
    const b = bar.current;
    return d && b ? Math.max(0, d.offsetHeight - b.offsetHeight) : 0;
  };
  /** Grow the surface to `o` px above the bar – straight on the elements, no re-render. */
  const place = (o: number, animate: boolean) => {
    const d = dock.current;
    if (!d || !bar.current || !title.current || !scrim.current) return;
    const full = room();
    const at = Math.max(0, Math.min(full, o));
    const t = full ? at / full : 0;
    const ease = '260ms cubic-bezier(0.2, 0.8, 0.2, 1)';
    for (const el of [d, bar.current, title.current, scrim.current]) {
      el.style.transition = animate ? `transform ${ease}, opacity ${ease}` : 'none';
    }
    d.style.transform = `translateY(${full - at}px)`;
    // the tabs fade out quickly, the title fades in – the bar becomes the header
    bar.current.style.opacity = String(Math.max(0, 1 - t * 1.8));
    title.current.style.opacity = String(Math.max(0, t * 1.8 - 0.8));
    scrim.current.style.opacity = String(t);
    offset.current = at;
  };
  const settle = (isOpen: boolean) => {
    window.clearTimeout(hideTimer.current);
    setShown(true);
    setOpenState(isOpen);
    place(isOpen ? Number.MAX_SAFE_INTEGER : 0, true);
    if (!isOpen) hideTimer.current = window.setTimeout(() => setShown(false), 280);
  };
  useEffect(() => () => window.clearTimeout(hideTimer.current), []);
  // the space the bar keeps in the page follows its real height (font size, safe area)
  useLayoutEffect(() => {
    const b = bar.current;
    if (!b) return;
    const measure = () => setBarHeight(b.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(b);
    return () => ro.disconnect();
  }, []);
  // closed position whenever the layout changes
  useLayoutEffect(() => {
    if (!open && !moving) place(0, false);
  });

  const drag = useVerticalDrag({
    direction: 'up',
    from: () => offset.current,
    onDragStart: () => {
      window.clearTimeout(hideTimer.current);
      setShown(true);
      setMoving(true);
    },
    onMove: (o) => place(o, false),
    onEnd: (o, v) => {
      settle(snapOpen(o, v, room()));
      // the release must not become a tap on the tab or tile under the finger
      window.setTimeout(() => setMoving(false), 0);
    },
  });

  // Escape closes; Tab stays inside while open; focus goes back to "Mehr"
  useEffect(() => {
    if (!open) return;
    const d = dock.current;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        settle(false);
        moreButton.current?.focus();
      }
      if (e.key === 'Tab' && d) {
        const f = [
          ...d.querySelectorAll<HTMLElement>('[role="dialog"] a[href], [role="dialog"] button'),
        ];
        if (!f.length) return;
        const first = f[0]!;
        const last = f[f.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const hiddenActive = !items.some((i) => i.id === activeId);
  const hiddenBadges = more.items
    .filter((i) => !items.some((f) => f.id === i.id))
    .reduce((n, i) => n + (i.badge ?? 0), 0);
  const close = () => settle(false);

  return (
    // keeps the bar's room in the page; the surface itself is fixed to the bottom
    <div className={cn('relative', className)} style={{ height: barHeight }}>
      <div
        ref={scrim}
        aria-hidden
        onClick={close}
        className={cn(
          'fixed inset-0 bg-surface-scrim opacity-0',
          open ? 'pointer-events-auto' : 'pointer-events-none',
        )}
      />
      <div
        ref={dock}
        onPointerDownCapture={drag}
        className={cn(
          'fixed inset-x-0 bottom-0 touch-none select-none rounded-t-sheet border-t border-border-subtle bg-surface-raised shadow-floating',
          noLinkDrag,
        )}
      >
        {/* the grip at the top edge – where a finger expects it */}
        <span
          aria-hidden
          className="pointer-events-none absolute top-inset-xs left-1/2 h-1 w-10 -translate-x-1/2 rounded-chip bg-border-strong"
        />
        <div
          role="dialog"
          aria-modal={open}
          aria-labelledby={headingId}
          inert={!open && !moving}
          className={cn('flex flex-col gap-gap-md px-inset-lg pb-safe', !shown && 'invisible')}
        >
          <div
            ref={title}
            className="flex items-center justify-between gap-gap-md opacity-0"
            style={{ minHeight: barHeight }}
          >
            <h2 id={headingId} className="font-display text-xl font-black tracking-tight">
              {uiText().allAreas}
            </h2>
            <IconButton
              icon={X}
              aria-label={uiText().close}
              onPress={() => {
                close();
                moreButton.current?.focus();
              }}
              className="-mr-inset-xs"
            />
          </div>
          <ul className="grid grid-cols-3 gap-gap-sm">
            {more.items.map((item) => {
              const active = item.id === activeId;
              return (
                <li key={item.id}>
                  <Link
                    href={item.href}
                    isDisabled={moving}
                    onPress={close}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      'relative flex min-h-touch-target cursor-pointer flex-col justify-between gap-gap-sm rounded-card bg-surface-sunken p-inset-sm font-bold outline-none',
                      'data-focus-visible:outline-3 data-focus-visible:outline-border-focus data-pressed:scale-97',
                      active && 'ring-2 ring-accent-primary',
                    )}
                  >
                    <span className="relative self-start">
                      <Icon
                        icon={item.icon}
                        size="lg"
                        weight={active ? 'fill' : 'duotone'}
                        className="text-accent-primary"
                      />
                      <Badge n={item.badge} />
                    </span>
                    {item.label}
                    {item.badge ? (
                      <span className="sr-only">{uiText().open(String(item.badge))}</span>
                    ) : null}
                  </Link>
                </li>
              );
            })}
          </ul>
          {more.links?.length ? (
            <ul className="flex flex-wrap gap-gap-sm pb-gap-md">
              {more.links.map((l) => (
                <li key={l.id}>
                  <Link
                    href={l.href}
                    isDisabled={moving}
                    onPress={close}
                    className="flex min-h-touch-min cursor-pointer items-center gap-gap-sm rounded-chip border border-border-default px-inset-sm font-semibold outline-none data-focus-visible:outline-3 data-focus-visible:outline-border-focus"
                  >
                    <Icon icon={l.icon} size="sm" />
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        {/* the tab row lies over the title band and fades out as the surface comes up */}
        <nav
          ref={bar}
          aria-label={label ?? uiText().mainNav}
          inert={open}
          className="absolute inset-x-0 top-0 grid auto-cols-fr grid-flow-col px-inset-xs pt-inset-xs pb-safe"
        >
          <Tabs items={items} activeId={activeId} disabled={moving} />
          <AriaButton
            ref={moreButton}
            isDisabled={moving}
            onPress={() => {
              settle(true);
              // keyboard and screen reader: straight to the first area
              window.setTimeout(
                () => dock.current?.querySelector<HTMLElement>('[role="dialog"] a[href]')?.focus(),
                0,
              );
            }}
            aria-expanded={open}
            aria-haspopup="dialog"
            className={tabClass(hiddenActive)}
          >
            {hiddenActive && <ActiveBar />}
            <span className="relative">
              <Icon icon={SquaresFour} size="md" weight={hiddenActive ? 'fill' : 'regular'} />
              <Badge n={hiddenBadges} />
            </span>
            {uiText().more}
            {hiddenBadges ? (
              <span className="sr-only">{uiText().open(String(hiddenBadges))}</span>
            ) : null}
          </AriaButton>
        </nav>
      </div>
    </div>
  );
}
